import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, Space, message, Popconfirm,
  Card, Typography, Tag, Alert, Descriptions, Select, DatePicker, Tabs
} from 'antd';
import dayjs from 'dayjs';
import {
  EditOutlined, DeleteOutlined, MedicineBoxOutlined,
  CalendarOutlined, SearchOutlined, EyeOutlined, PlusOutlined
} from '@ant-design/icons';
import { useDebouncedSearch } from '../hooks/useDebounce';
import apiClient from '../config/axios';
import PatientSearch from './PatientSearch';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Patient } from '../services/patientService';

const { Title } = Typography;

// Default prices for testing - should be replaced with proper pricing logic
const getDefaultPrice = (medicineName: string) => {
  const priceMap: { [key: string]: number } = {
    'Test Medicine Proper 150118': 25.50,
    'Test Medicine 145928': 35.75,
    'Test Medicine': 55.25,
    'DKC': 15.00
  };
  return priceMap[medicineName || ''] || 0;
};

// Calculate in-stock quantity after subtracting expired and destroyed quantities
const calculateInStockQuantity = (currentStock: number, expiredQuantity: number = 0, destroyedQuantity: number = 0) => {
  return Math.max(0, currentStock - expiredQuantity - destroyedQuantity);
};

interface Medicine {
  medicine_id: number;
  medicine_name: string;
  manufacturer?: string;
  batch_number?: string;
  expiry_date?: string;
  unit_price: number | string;
  hike_percent?: number;
  discount_percent?: number;
  quantity_available: number;
  expired_quantity?: number;
  destroyed_quantity?: number;
  min_stock_level?: number;
  category_name: string;
  category_id?: number;
  unit_type?: string;
  notes?: string;
  // Seller information
  seller_id?: number;
  seller_name?: string;
  seller_contact_person?: string;
  seller_phone?: string;
  seller_medicine_id?: number;
  seller_stock_quantity?: number;
  deleted_at?: string;
  is_active?: boolean;
  status?: string;
  branch_medicine_id?: number; // For branch medicines - the actual branch medicine record ID
}

interface MedicineManagementProps {
  onAddToCart?: (medicine: Medicine, quantity: number) => void;
  showAddToCart?: boolean;
  branchId?: number; // If provided, fetch branch-specific inventory
  filterType?: 'branch' | 'seller' | 'medicine' | 'seller_medicine'; // Type of filtering
  filterId?: number; // ID for the filter (branch_id, seller_id, medicine_id, or seller_medicine_id)
}

const MedicineManagement: React.FC<MedicineManagementProps> = ({ 
  onAddToCart, 
  showAddToCart = false,
  branchId,
  filterType,
  filterId
}) => {
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [filteredMedicines, setFilteredMedicines] = useState<Medicine[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState<Medicine | null>(null);
  const [form] = Form.useForm();
  const [addToCartQuantity, setAddToCartQuantity] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>('');
  const [showDeleted, setShowDeleted] = useState<boolean>(false);
  const [addToCartMode, setAddToCartMode] = useState<Record<number, boolean>>({});
  const [cartQuantities, setCartQuantities] = useState<Record<number, number>>({});
  const [addMedicineModalVisible, setAddMedicineModalVisible] = useState(false);
  const [addMedicineForm] = Form.useForm();
  const [createMedicineForm] = Form.useForm();
  const [availableMedicines, setAvailableMedicines] = useState<any[]>([]);
  const [availableSellers, setAvailableSellers] = useState<any[]>([]);
  const [availableCategories, setAvailableCategories] = useState<any[]>([]);
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null);
  const [loadingMedicines, setLoadingMedicines] = useState(false);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [isCreatingNewMedicine, setIsCreatingNewMedicine] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categoryForm] = Form.useForm();
  
  // User role detection state
  const [userRole, setUserRole] = useState<'seller' | 'branch' | 'admin' | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  
  // Get selected patient from Redux store
  const { selectedPatient } = useSelector((state: RootState) => state.patient);
  
  // Debounce search text to avoid filtering on every keystroke
  const debouncedSearchText = useDebouncedSearch(searchText, 300);

  // Initialize user role and ID detection
  useEffect(() => {
    const initializeUser = () => {
      try {
        const userStr = localStorage.getItem('user');
        console.log('MedicineManagement: Raw user string:', userStr);
        
        if (userStr) {
          const user = JSON.parse(userStr);
          console.log('MedicineManagement: Parsed user object:', user);
          
          if (user.is_main_seller === true) {
            console.log('MedicineManagement: User role determined as: seller');
            setUserRole('seller');
            setCurrentUserId(user.id);
          } else if (user.branch_id) {
            console.log('MedicineManagement: User role determined as: branch, branch_id:', user.branch_id);
            setUserRole('branch');
            setCurrentUserId(user.branch_id);
          } else {
            console.log('MedicineManagement: User role determined as: admin');
            setUserRole('admin');
            setCurrentUserId(user.id);
          }
        } else {
          console.log('MedicineManagement: No user string found in localStorage');
          setUserRole(null);
        }
      } catch (error) {
        console.error('MedicineManagement: Error parsing user data:', error);
        setUserRole(null);
      }
    };

    initializeUser();
  }, []);

  // Function to fetch category data for medicines
  const fetchCategories = useCallback(async (medicineIds: number[]) => {
    try {
      console.log('fetchCategories: Starting with medicine IDs:', medicineIds);
      
      // Filter out any undefined or null values
      const validMedicineIds = medicineIds.filter(id => id !== undefined && id !== null);
      console.log('fetchCategories: Valid medicine IDs:', validMedicineIds);
      
      if (validMedicineIds.length === 0) {
        console.log('fetchCategories: No valid medicine IDs to fetch');
        return new Map();
      }
      
      // Fetch medicine details with categories
      const medicinePromises = validMedicineIds.map(async (medicineId) => {
        try {
          console.log(`fetchCategories: Fetching category for medicine ID ${medicineId}`);
          const response = await apiClient.get(`/medicines/${medicineId}`);
          
          console.log(`fetchCategories: Response for medicine ID ${medicineId}:`, response.data);
          
          if (response.data.data) {
            const medicine = response.data.data;
            const categoryName = medicine.attributes?.category?.data?.attributes?.category_name || 'No Category';
            console.log(`fetchCategories: Category for medicine ID ${medicineId}:`, categoryName);
            
            return {
              medicine_id: medicineId,
              category_name: categoryName
            };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching category for medicine ID ${medicineId}:`, error);
          return null;
        }
      });
      
      const categoryData = await Promise.all(medicinePromises);
      console.log('fetchCategories: Category data:', categoryData);
      
      const categoryMap = new Map();
      
      categoryData.forEach(data => {
        if (data) {
          categoryMap.set(data.medicine_id, data.category_name);
        }
      });
      
      console.log('fetchCategories: Category map:', categoryMap);
      return categoryMap;
    } catch (error) {
      console.error('Error fetching medicine categories:', error);
      return new Map();
    }
  }, []);

  // Function to fetch unit prices from seller-medicines
  const fetchPrices = useCallback(async (medicineIds: number[]) => {
    try {
      console.log('fetchPrices: Starting with medicine IDs:', medicineIds);
      
      // Filter out any undefined or null values
      const validMedicineIds = medicineIds.filter(id => id !== undefined && id !== null);
      console.log('fetchPrices: Valid medicine IDs:', validMedicineIds);
      
      if (validMedicineIds.length === 0) {
        console.log('fetchPrices: No valid medicine IDs to fetch');
        return new Map();
      }
      
      // Fetch seller-medicines for these medicine IDs
      const sellerMedicinePromises = validMedicineIds.map(async (medicineId) => {
        try {
          console.log(`fetchPrices: Fetching price for medicine ID ${medicineId}`);
          const response = await apiClient.get('/seller-medicines', {
            params: {
              'medicine_id': medicineId,
              'pagination[pageSize]': 1
            }
          });
          
          console.log(`fetchPrices: Response for medicine ID ${medicineId}:`, response.data);
          
          if (response.data.data && response.data.data.length > 0) {
            const sellerMedicine = response.data.data[0];
            const unitPrice = sellerMedicine.attributes?.unit_price || 0;
            const sellerName = sellerMedicine.attributes?.seller?.data?.attributes?.seller_name || 'Unknown Seller';
            const expiryDate = sellerMedicine.attributes?.expiry_date || null;
            
            console.log(`fetchPrices: Price for medicine ID ${medicineId}:`, unitPrice, 'from seller:', sellerName, 'expiry:', expiryDate);
            
            return {
              medicine_id: medicineId,
              unit_price: unitPrice,
              seller_name: sellerName,
              expiry_date: expiryDate
            };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching seller medicine for medicine ID ${medicineId}:`, error);
          return null;
        }
      });
      
      const sellerMedicineData = await Promise.all(sellerMedicinePromises);
      console.log('fetchPrices: Seller medicine data:', sellerMedicineData);
      
      const priceMap = new Map();
      
      sellerMedicineData.forEach(data => {
        if (data) {
          priceMap.set(data.medicine_id, {
            unit_price: data.unit_price,
            seller_name: data.seller_name,
            expiry_date: data.expiry_date
          });
        }
      });
      
      console.log('fetchPrices: Price map:', priceMap);
      return priceMap;
    } catch (error) {
      console.error('Error fetching seller medicine prices:', error);
      return new Map();
    }
  }, []);

  // Function to fetch additional data (categories and prices) for medicines
  const fetchAdditionalData = useCallback(async (medicines: any[]) => {
    try {
      console.log('fetchAdditionalData: Starting with medicines:', medicines);
      
      // Get unique medicine IDs, filtering out undefined values
      const medicineIds = Array.from(new Set(medicines.map(m => m.medicine_id).filter(id => id !== undefined && id !== null)));
      console.log('fetchAdditionalData: Medicine IDs:', medicineIds);
      
      // Fetch both categories and prices in parallel
      const [categoryData, priceData] = await Promise.all([
        fetchCategories(medicineIds),
        fetchPrices(medicineIds)
      ]);
      
      console.log('fetchAdditionalData: Category data:', categoryData);
      console.log('fetchAdditionalData: Price data:', priceData);
      
      // Update medicines with both category and price data
      const updatedMedicines = medicines.map(medicine => {
        const categoryName = categoryData.get(medicine.medicine_id);
        const priceInfo = priceData.get(medicine.medicine_id);
        
        return {
          ...medicine,
          category_name: categoryName || medicine.category_name || 'No Category',
          unit_price: priceInfo?.unit_price || medicine.unit_price || 0,
          seller_name: priceInfo?.seller_name || medicine.seller_name || 'Unknown Seller',
          expiry_date: priceInfo?.expiry_date || medicine.expiry_date || null
        };
      });
      
      // Remove duplicates based on medicine_id to prevent duplicate entries
      const uniqueMedicines = updatedMedicines.filter((medicine: any, index: number, self: any[]) => 
        index === self.findIndex((m: any) => m.medicine_id === medicine.medicine_id)
      );
      
      console.log('fetchAdditionalData: Updated medicines:', uniqueMedicines);
      setMedicines(uniqueMedicines);
      console.log('MedicineManagement: Updated with additional data');
    } catch (error) {
      console.error('Error fetching additional data:', error);
    }
  }, [fetchCategories, fetchPrices]);

  // Function to fetch prices for branch medicines that don't have seller_medicine data
  const fetchPricesForBranchMedicines = useCallback(async (medicines: any[]) => {
    try {
      console.log('fetchPricesForBranchMedicines: Starting for medicines:', medicines);
      
      const medicinesWithoutPrices = medicines.filter(med => !med.unit_price || med.unit_price === 0);
      if (medicinesWithoutPrices.length === 0) {
        console.log('fetchPricesForBranchMedicines: No medicines need price fetching');
        return;
      }
      
      const medicineIds = medicinesWithoutPrices.map(med => med.medicine_id).filter(id => id !== undefined && id !== null);
      console.log('fetchPricesForBranchMedicines: Medicine IDs needing prices:', medicineIds);
      
      if (medicineIds.length === 0) {
        console.log('fetchPricesForBranchMedicines: No valid medicine IDs to fetch prices for');
        return;
      }
      
      // Fetch seller_medicines for these medicine IDs
      const filterParams: any = {};
      medicineIds.forEach((id, index) => {
        filterParams[`filters[$or][${index}][medicine][id][$eq]`] = id;
      });
      
      const response = await apiClient.get('/seller-medicines', {
        params: {
          ...filterParams,
          'pagination[pageSize]': 100
        }
      });
      
      console.log('fetchPricesForBranchMedicines: Seller medicines response:', response.data);
      
      if (response.data.data && response.data.data.length > 0) {
        // Create a map of medicine_id to price data
        const priceMap = new Map();
        response.data.data.forEach((sellerMed: any) => {
          const medicineId = sellerMed.attributes?.medicine?.data?.id;
          const unitPrice = sellerMed.attributes?.unit_price;
          const sellerName = sellerMed.attributes?.seller?.data?.attributes?.seller_name;
          
          if (medicineId && unitPrice) {
            // Use the first price found for each medicine
            if (!priceMap.has(medicineId)) {
              priceMap.set(medicineId, {
                unit_price: unitPrice,
                seller_name: sellerName || 'Unknown Seller'
              });
            }
          }
        });
        
        console.log('fetchPricesForBranchMedicines: Price map:', priceMap);
        
        // Update medicines with prices
        medicines.forEach(medicine => {
          if ((!medicine.unit_price || medicine.unit_price === 0) && priceMap.has(medicine.medicine_id)) {
            const priceData = priceMap.get(medicine.medicine_id);
            medicine.unit_price = priceData.unit_price;
            medicine.seller_name = priceData.seller_name;
            console.log(`fetchPricesForBranchMedicines: Updated medicine ${medicine.medicine_name} with price ${priceData.unit_price}`);
          }
        });
      }
    } catch (error) {
      console.error('Error fetching prices for branch medicines:', error);
    }
  }, []);

  const fetchMedicines = useCallback(async () => {
    try {
      // Determine endpoint and parameters based on user role and props
      let endpoint: string;
      let params: any = {};
      
      console.log('MedicineManagement: fetchMedicines - userRole:', userRole, 'branchId:', branchId, 'currentUserId:', currentUserId);
      
      // Determine which API to call based on user role
      if (userRole === 'branch' || branchId) {
        // For branch users, use branch-medicines endpoint
        endpoint = '/branch-medicines';
        const targetBranchId = branchId || currentUserId;
        params['filters[branch][id][$eq]'] = targetBranchId;
        
        // Add other query parameters as defined in the routes
        if (showDeleted) {
          params.include_deleted = 'true';
        }
        
        // Add search parameter if search text exists (server-side search)
        if (debouncedSearchText.trim() !== '') {
          params['search'] = debouncedSearchText.trim();
        }
        
        // Add pagination parameters - send in simple format that Express can parse easily
        params['page'] = pagination.current;
        params['pageSize'] = pagination.pageSize;
        
        // Add type and id parameters if provided
        if (filterType && filterId) {
          if (filterType === 'medicine') {
            params['filters[medicine][id][$eq]'] = filterId;
          } else if (filterType === 'seller_medicine') {
            params['filters[seller_medicine][id][$eq]'] = filterId;
          }
        }
      } else if (userRole === 'seller') {
        // For seller users, use seller-medicines endpoint
        endpoint = '/seller-medicines';
        params['filters[seller][id][$eq]'] = currentUserId;
        
        if (showDeleted) {
          params.include_deleted = 'true';
        }
        
        // Add search parameter if search text exists (server-side search)
        if (debouncedSearchText.trim() !== '') {
          params['search'] = debouncedSearchText.trim();
        }
        
        // Add pagination parameters - send in simple format that Express can parse easily
        params['page'] = pagination.current;
        params['pageSize'] = pagination.pageSize;
      } else {
        // No global medicines fetch; if not branch/seller context, return empty set
        setMedicines([]);
        setFilteredMedicines([]);
        setLoading(false);
        return;
      }
      
      console.log('MedicineManagement: Fetching from', endpoint, 'params:', params);
      console.log('MedicineManagement: branchId provided:', branchId);
      console.log('MedicineManagement: Using branch endpoint:', !!branchId);
      
      const response = await apiClient.get(endpoint, { params });
      
      console.log('MedicineManagement: Response data length:', response.data.data?.length || 0);
      console.log('MedicineManagement: First few items:', response.data.data?.slice(0, 2) || []);
      console.log('MedicineManagement: Response meta:', response.data.meta);
      
      // Update pagination total from API response
      if (response.data.meta?.pagination) {
        setPagination(prev => ({
          ...prev,
          total: response.data.meta.pagination.total || 0
        }));
      }
      
      // Check if no medicines found for branch
      if (branchId && (!response.data.data || response.data.data.length === 0)) {
        console.log('MedicineManagement: No medicines found for branch', branchId);
        setMedicines([]);
        return;
      }
      
      // Normalize the data structure to ensure consistent field names
      const normalizedData = (response.data.data || []).map((item: any) => {
        if (userRole === 'branch' || branchId) {
          // Branch medicine data structure - access nested attributes correctly
          const medicineData = item.attributes?.medicine?.data?.attributes;
          const categoryData = item.attributes?.medicine?.data?.attributes?.category?.data?.attributes;
          const sellerMedicineData = item.attributes?.seller_medicine?.data?.attributes;
          const sellerData = item.attributes?.seller_medicine?.data?.attributes?.seller?.data?.attributes;
          const branchData = item.attributes;
          
          console.log('MedicineManagement: Processing branch medicine item:', item);
          console.log('MedicineManagement: Medicine data:', medicineData);
          console.log('MedicineManagement: Category data:', categoryData);
          console.log('MedicineManagement: Seller medicine data:', sellerMedicineData);
          console.log('MedicineManagement: Seller data:', sellerData);
          console.log('MedicineManagement: Branch data:', branchData);
          
          const processedItem = {
            ...item,
            medicine_id: item.attributes?.medicine?.data?.id || item.medicine_id,
            branch_medicine_id: item.id, // The actual branch medicine record ID
            medicine_name: medicineData?.medicine_name || 'Unknown Medicine',
            category_name: categoryData?.category_name || 'No Category',
            manufacturer: medicineData?.manufacturer || 'Unknown',
            description: medicineData?.medicine_description || '',
            unit_of_measurement: medicineData?.unit_of_measurement || '',
            // Branch medicine specific fields
            quantity_available: branchData?.current_stock || 0,
            // Note: unit_price comes from seller_medicine or should be set in branch_medicine
            // For branch medicines without seller_medicine, use 0 or fetch from seller_medicines
            unit_price: sellerMedicineData?.unit_price || 0,
            batch_number: sellerMedicineData?.batch_number || 'N/A',
            status: branchData?.status || 'active',
            discount_percent: sellerMedicineData?.discount_percent || branchData?.discount_percent || 0,
            hike_percent: sellerMedicineData?.hike_percent || branchData?.hike_percent || 0,
            expiry_date: sellerMedicineData?.expiry_date || branchData?.expiry_date || null,
            first_order_date: branchData?.first_ordered_date,
            last_restocked_date: branchData?.last_restocked_date,
            notes: branchData?.notes || '',
            destroyed_quantity: branchData?.destroyed_quantity || 0,
            expired_quantity: branchData?.expired_quantity || 0,
            // Seller information
            seller_id: sellerData?.id || null,
            seller_name: sellerData?.seller_name || 'Unknown Seller',
            seller_contact_person: sellerData?.contact_person || '',
            seller_phone: sellerData?.phone_number || '',
            seller_medicine_id: sellerMedicineData?.id || null,
            seller_stock_quantity: sellerMedicineData?.seller_stock_quantity || 0,
            created_at: item.createdAt || item.created_at,
            updated_at: item.updatedAt || item.updated_at,
          };
          
          console.log('MedicineManagement: Processed item:', processedItem);
          console.log('MedicineManagement: Final unit_price:', processedItem.unit_price);
          console.log('MedicineManagement: Final category_name:', processedItem.category_name);
          return processedItem;
        } else if (userRole === 'seller') {
          // Seller medicine data structure
          const medicineData = item.attributes?.medicine?.data?.attributes;
          const categoryData = item.attributes?.medicine?.data?.attributes?.category?.data?.attributes;
          const sellerData = item.attributes?.seller?.data?.attributes;
          const sellerMedicineData = item.attributes;
          
          console.log('MedicineManagement: Processing seller medicine item:', item);
          console.log('MedicineManagement: Medicine data:', medicineData);
          console.log('MedicineManagement: Category data:', categoryData);
          console.log('MedicineManagement: Seller data:', sellerData);
          console.log('MedicineManagement: Seller medicine data:', sellerMedicineData);
          
          const processedItem = {
            ...item,
            medicine_id: item.attributes?.medicine?.data?.id || item.medicine_id,
            seller_medicine_id: item.id, // The actual seller medicine record ID
            medicine_name: medicineData?.medicine_name || 'Unknown Medicine',
            category_name: categoryData?.category_name || 'No Category',
            manufacturer: medicineData?.manufacturer || 'Unknown',
            description: medicineData?.medicine_description || '',
            unit_of_measurement: medicineData?.unit_of_measurement || '',
            // Seller medicine specific fields
            quantity_available: sellerMedicineData?.seller_stock_quantity || 0,
            unit_price: sellerMedicineData?.unit_price || 0,
            batch_number: sellerMedicineData?.batch_number || 'N/A',
            status: sellerMedicineData?.is_active ? 'active' : 'inactive',
            discount_percent: sellerMedicineData?.discount_percentage || 0,
            hike_percent: sellerMedicineData?.hike_percent || 0,
            expiry_date: sellerMedicineData?.expiry_date || null,
            expired_quantity: sellerMedicineData?.expired_quantity || 0,
            destroyed_quantity: sellerMedicineData?.destroyed_quantity || 0,
            // Seller information
            seller_id: sellerData?.id || null,
            seller_name: sellerData?.seller_name || 'Unknown Seller',
            seller_contact_person: sellerData?.contact_person || '',
            seller_phone: sellerData?.phone_number || '',
            created_at: item.createdAt || item.created_at,
            updated_at: item.updatedAt || item.updated_at,
          };
          
          console.log('MedicineManagement: Processed seller medicine item:', processedItem);
          return processedItem;
        } else {
          // Global medicine data structure
          return {
            ...item,
            quantity_available: item.quantity_available || item.quantity || 0,
            manufacturer: item.manufacturer || 'Unknown',
            batch_number: item.batch_number || 'N/A'
          };
        }
      });
      
      // Remove duplicates based on medicine_id to prevent duplicate entries from API
      const uniqueNormalizedData = normalizedData.filter((medicine: any, index: number, self: any[]) => 
        index === self.findIndex((m: any) => m.medicine_id === medicine.medicine_id)
      );
      
      // For branch medicines without seller_medicine data, fetch prices from seller_medicines
      if (branchId) {
        const medicinesWithoutPrices = uniqueNormalizedData.filter((med: any) => !med.unit_price || med.unit_price === 0);
        if (medicinesWithoutPrices.length > 0) {
          console.log('MedicineManagement: Found medicines without prices, fetching from seller_medicines:', medicinesWithoutPrices);
          await fetchPricesForBranchMedicines(uniqueNormalizedData);
        }
      }
      
      setMedicines(uniqueNormalizedData);
      console.log('MedicineManagement: Loaded', uniqueNormalizedData.length, 'unique medicines');
      
      // For branch medicines, we already have all the data we need from the branch-medicines endpoint
      // No need to fetch additional data as it would overwrite the branch-specific information
      if (!branchId && uniqueNormalizedData.length > 0) {
        // Only fetch additional data for global medicines (when no branchId)
        await fetchAdditionalData(uniqueNormalizedData);
      }
    } catch (error: any) {
      const apiType = userRole === 'branch' ? 'branch medicines' : userRole === 'seller' ? 'seller medicines' : 'medicines';
      console.error(`Error fetching ${apiType}:`, error);
      console.error('Error details:', error.response?.data || error.message);
      message.error(error.response?.data?.message || error.message || `Failed to fetch ${apiType}`);
    } finally {
      setLoading(false);
    }
  }, [userRole, currentUserId, branchId, showDeleted, filterType, filterId, pagination.current, pagination.pageSize, debouncedSearchText, fetchAdditionalData, fetchPricesForBranchMedicines]);

  useEffect(() => {
    fetchMedicines();
  }, [fetchMedicines]); // Re-fetch when any filter changes

  // Reset pagination to page 1 when search text changes (since search is now server-side)
  useEffect(() => {
    if (debouncedSearchText.trim() !== '') {
      // Reset to page 1 when starting a new search
      setPagination(prev => {
        if (prev.current !== 1) {
          return { ...prev, current: 1 };
        }
        return prev;
      });
    }
  }, [debouncedSearchText]);

  // Since search is now server-side, we don't need client-side filtering
  // Just use the medicines directly from the API response
  useEffect(() => {
    setFilteredMedicines(medicines);
  }, [medicines]);


  const handleEdit = (medicine: Medicine) => {
    setSelectedMedicine(medicine);
    form.setFieldsValue({
      unit_price: typeof medicine.unit_price === 'string' ? parseFloat(medicine.unit_price) : (medicine.unit_price || 0),
      hike_percent: medicine.hike_percent || 0,
      discount_percent: medicine.discount_percent || 0,
      expired_quantity: medicine.expired_quantity || 0,
      destroyed_quantity: medicine.destroyed_quantity || 0,
      quantity_available: medicine.quantity_available || 0,
      notes: medicine.notes || ''
    });
    setEditModalVisible(true);
  };

  const handleView = (medicine: Medicine) => {
    setSelectedMedicine(medicine);
    setViewModalVisible(true);
  };

  const handleUpdate = async (values: any) => {
    try {
      if (branchId && selectedMedicine?.branch_medicine_id) {
        // For branch medicines, use the branch_medicine_id
        const endpoint = `/branch-medicines/${selectedMedicine.branch_medicine_id}`;
        // Only send fields that exist on branch_medicine table
        // unit_price is not on branch_medicine (it comes from seller_medicine)
        const updateData = {
          current_stock: values.quantity_available,
          hike_percent: values.hike_percent || 0,
          discount_percent: values.discount_percent || 0,
          expired_quantity: values.expired_quantity || 0,
          destroyed_quantity: values.destroyed_quantity || 0,
          notes: values.notes || ''
        };
        await apiClient.put(endpoint, updateData);
      } else {
        // For global medicines
        const endpoint = `/medicines/${selectedMedicine?.medicine_id}`;
        await apiClient.put(endpoint, values);
      }
      
      message.success('Medicine updated successfully');
      setEditModalVisible(false);
      fetchMedicines();
    } catch (error) {
      console.error('Error updating medicine:', error);
      message.error('Failed to update medicine');
    }
  };

  const handleDelete = async (medicineId: number) => {
    try {
      if (branchId && selectedMedicine?.branch_medicine_id) {
        // For branch medicines, use the branch_medicine_id
        const endpoint = `/branch-medicines/${selectedMedicine.branch_medicine_id}`;
        await apiClient.delete(endpoint);
      } else {
        // For global medicines
        const endpoint = `/medicines/${medicineId}`;
        await apiClient.delete(endpoint);
      }
      
      message.success('Medicine deleted successfully');
      fetchMedicines();
    } catch (error) {
      message.error('Failed to delete medicine');
    }
  };

  const handleRestore = async (medicineId: number) => {
    try {
      if (branchId) {
        // Branch medicines don't have a restore endpoint, so we'll show a message
        message.info('Restore functionality not available for branch medicines');
        return;
      } else {
        // For global medicines
        const endpoint = `/medicines/${medicineId}/restore`;
        await apiClient.post(endpoint);
        message.success('Medicine restored successfully');
        fetchMedicines();
      }
    } catch (error) {
      message.error('Failed to restore medicine');
    }
  };


  const handleAddToCart = (medicine?: Medicine, quantity?: number) => {
    const medicineToAdd = medicine || selectedMedicine;
    const quantityToAdd = quantity || addToCartQuantity;
    
    if (medicineToAdd && onAddToCart) {
      onAddToCart(medicineToAdd, quantityToAdd);
      setAddToCartQuantity(1);
      if (!medicine) {
        setViewModalVisible(false);
      }
    }
  };

  const toggleAddToCartMode = (branchMedicineId: number) => {
    setAddToCartMode(prev => ({
      ...prev,
      [branchMedicineId]: !prev[branchMedicineId]
    }));
    if (!addToCartMode[branchMedicineId]) {
      setCartQuantities(prev => ({
        ...prev,
        [branchMedicineId]: 1
      }));
    }
  };

  const handleQuantityChange = (branchMedicineId: number, quantity: number) => {
    setCartQuantities(prev => ({
      ...prev,
      [branchMedicineId]: quantity
    }));
  };

  const handleAddToCartFromInput = (medicine: Medicine) => {
    const quantity = cartQuantities[medicine.branch_medicine_id || 0] || 1;
    if (quantity > 0) {
      handleAddToCart(medicine, quantity);
      setAddToCartMode(prev => ({
        ...prev,
        [medicine.branch_medicine_id || 0]: false
      }));
      message.success(`${medicine.medicine_name} (${quantity} units) added to cart`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, medicine: Medicine) => {
    if (e.key === 'Enter') {
      handleAddToCartFromInput(medicine);
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    message.success(`Patient selected: ${patient.name}`);
    console.log('Selected patient:', patient);
  };

  // Functions for Add Medicine functionality
  const handleOpenAddMedicineModal = () => {
    console.log('Opening add medicine modal...');
    console.log('branchId:', branchId);
    setAddMedicineModalVisible(true);
    setIsCreatingNewMedicine(false);
    // Fetch medicines, sellers, and categories when opening modal
    fetchAvailableMedicines();
    fetchAvailableSellers();
    fetchAvailableCategories();
    addMedicineForm.resetFields();
  };

  const fetchAvailableMedicines = async () => {
    setLoadingMedicines(true);
    try {
      // In branch context, fetch medicines from branch inventory
      if (userRole === 'branch' || branchId) {
        const targetBranchId = branchId || currentUserId;
        const response = await apiClient.get('/branch-medicines', {
          params: {
            'filters[branch][id][$eq]': targetBranchId,
            pageSize: 1000
          }
        });

        const medicinesData = (response.data.data || []).map((item: any) => ({
          id: item.id, // branch_medicine id
          name: item.attributes?.medicine?.data?.attributes?.medicine_name || 'Unknown Medicine',
          category: item.attributes?.medicine?.data?.attributes?.category?.data?.attributes?.category_name || 'No Category'
        }));

        setAvailableMedicines(medicinesData);
      } else {
        // No global fetch in this component
        setAvailableMedicines([]);
      }
    } catch (error) {
      console.error('Error fetching medicines:', error);
      message.error('Failed to fetch available medicines');
    } finally {
      setLoadingMedicines(false);
    }
  };

  const fetchAvailableSellers = async () => {
    setLoadingSellers(true);
    try {
      const response = await apiClient.get('/sellers', {
        params: {
          'filters[is_active][$eq]': true,
          'pagination[pageSize]': 1000
        }
      });
      
      console.log('Sellers API response:', response.data);
      
      const sellersData = response.data.data.map((item: any) => {
        console.log('Mapping seller item:', item);
        return {
          id: item.id,
          name: item.attributes?.seller_name || 'Unknown Seller',
          contact: item.attributes?.contact_person || ''
        };
      });
      
      console.log('Parsed sellers data:', sellersData);
      console.log('Setting availableSellers with:', sellersData);
      
      setAvailableSellers(sellersData);
    } catch (error) {
      console.error('Error fetching sellers:', error);
      message.error('Failed to fetch available sellers');
    } finally {
      setLoadingSellers(false);
    }
  };

  const fetchAvailableCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await apiClient.get('/categories', {
        params: {
          'pagination[pageSize]': 1000
        }
      });
      
      const categoriesData = response.data.data.map((item: any) => ({
        id: item.id,
        name: item.attributes?.category_name || 'Unknown Category',
        description: item.attributes?.description || ''
      }));
      
      setAvailableCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Failed to fetch available categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleAddMedicineSubmit = async (values: any) => {
    console.log('=== Medicine Form Submission Started ===');
    console.log('Form values:', values);
    console.log('Is creating new medicine:', isCreatingNewMedicine);
    console.log('Branch ID:', branchId);
    
    try {
      // Since we removed the "Add Existing Medicine" tab, always create new medicine
      // The old path is here for backward compatibility but should not be used
      if (true || isCreatingNewMedicine) {
        // Create new medicine first
        const medicineData = {
          medicine_name: values.medicine_name,
          category: values.category_id,
          medicine_description: values.description || '',
          manufacturer: values.manufacturer || ''
        };

        const medicineResponse = await apiClient.post('/medicines', medicineData);
        console.log('=== Medicine Creation Response ===');
        console.log('Full response:', medicineResponse.data);
        console.log('Response data:', medicineResponse.data?.data);
        
        // Backend returns: { status, message, data: { id, attributes } }
        const createdMedicine = medicineResponse.data.data;
        console.log('Created medicine object:', createdMedicine);
        
        // Handle different response structures
        let medicineId;
        if (createdMedicine?.id) {
          medicineId = createdMedicine.id;
        } else if (medicineResponse.data?.data?.id) {
          medicineId = medicineResponse.data.data.id;
        } else if (createdMedicine?.attributes?.id) {
          medicineId = createdMedicine.attributes.id;
        }
        
        console.log('Extracted Medicine ID:', medicineId);
        
        if (!medicineId) {
          throw new Error('Medicine ID not found in response');
        }

        // Create seller_medicine record (seller is now required)
        const batchNumber = `BATCH_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const sellerMedicineData = {
          batch_number: batchNumber,
          seller: values.seller,
          medicine: medicineId,
          unit_price: values.unit_price || 0,
          seller_stock_quantity: values.current_stock || 0,
          expiry_date: values.expiry_date ? values.expiry_date.format('YYYY-MM-DD') : null,
          discount_percentage: values.discount_percent || 0,
          hike_percent: values.hike_percent || 0,
          is_active: true,
          destroyed_quantity: 0,
          expired_quantity: 0,
        };

        const sellerMedicineResponse = await apiClient.post('/seller-medicines', sellerMedicineData);
        console.log('=== Seller Medicine Creation Response ===');
        console.log('Full response:', sellerMedicineResponse.data);
        
        // Backend returns: { status, message, data: { id, attributes } }
        const createdSellerMedicine = sellerMedicineResponse.data.data;
        console.log('Created seller medicine object:', createdSellerMedicine);
        
        let sellerMedicineId;
        if (createdSellerMedicine?.id) {
          sellerMedicineId = createdSellerMedicine.id;
        } else if (sellerMedicineResponse.data?.data?.id) {
          sellerMedicineId = sellerMedicineResponse.data.data.id;
        } else if (createdSellerMedicine?.attributes?.id) {
          sellerMedicineId = createdSellerMedicine.attributes.id;
        }
        
        console.log('Extracted Seller Medicine ID:', sellerMedicineId);
        
        if (!sellerMedicineId) {
          throw new Error('Seller medicine ID not found in response');
        }

        // Create branch_medicine record
        // Note: unit_price is NOT stored in branch_medicine, it comes from seller_medicine
        const branchMedicinePayload: any = {
          branch: branchId,
          medicine: medicineId,
          current_stock: values.current_stock || 0,
          hike_percent: values.hike_percent || 0,
          discount_percent: values.discount_percent || 0,
          notes: values.notes || '',
          expired_quantity: 0,
          destroyed_quantity: 0,
          status: 'active'
        };
        
        // Add seller_medicine reference (seller is now required)
        branchMedicinePayload.seller_medicine = sellerMedicineId;

        console.log('=== Sending Branch Medicine Payload ===');
        console.log('Payload object:', branchMedicinePayload);
        console.log('Branch ID:', branchId);
        console.log('Medicine ID:', medicineId);
        console.log('Seller Medicine ID:', sellerMedicineId);
        console.log('Current Stock:', values.current_stock);
        console.log('Payload JSON:', JSON.stringify(branchMedicinePayload, null, 2));

        const branchResponse = await apiClient.post('/branch-medicines', branchMedicinePayload);
        console.log('Branch medicine creation response:', branchResponse.data);
        
        message.success('New medicine created and added to branch inventory successfully');
      } else {
        // Add existing medicine to branch
        // Note: unit_price is NOT stored in branch_medicine, it comes from seller_medicine
        console.log('=== Adding Existing Medicine to Branch ===');
        console.log('Form values:', values);
        console.log('Medicine ID from form:', values.medicine_id);
        console.log('Branch ID:', branchId);
        
        const payload = {
          branch: branchId,
          medicine: values.medicine_id,
          current_stock: values.current_stock || 0,
          hike_percent: values.hike_percent || 0,
          discount_percent: values.discount_percent || 0,
          notes: values.notes || '',
          expired_quantity: 0,
          destroyed_quantity: 0,
          status: 'active'
        };
        
        console.log('Payload being sent:', payload);
        console.log('Payload JSON:', JSON.stringify(payload, null, 2));

        await apiClient.post('/branch-medicines', payload);
        
        message.success('Medicine added to branch inventory successfully');
      }
      
      setAddMedicineModalVisible(false);
      addMedicineForm.resetFields();
      createMedicineForm.resetFields();
      setIsCreatingNewMedicine(false);
      
      // Refresh the medicines list
      fetchMedicines();
    } catch (error: any) {
      console.error('Error adding medicine to branch:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error details:', error.response?.data);
      console.error('Full error object:', JSON.stringify(error.response?.data, null, 2));
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error?.message || 
                          error.response?.data?.error ||
                          JSON.stringify(error.response?.data) ||
                          error.message || 
                          'Failed to add medicine to branch';
      
      message.error(errorMessage);
    }
  };

  // Category creation handler
  const handleCategoryCreate = async () => {
    try {
      const values = await categoryForm.validateFields();
      
      const categoryData = {
        category_name: values.category_name,
        description: values.description || ''
      };
      
      const response = await apiClient.post('/categories', categoryData);
      
      // Extract category info from response (API handler returns data directly)
      const categoryInfo = response.data.data || response.data;
      const newCategory = {
        id: categoryInfo.id || categoryInfo.attributes?.id,
        name: categoryInfo.attributes?.category_name || values.category_name,
        description: values.description || ''
      };
      
      // Add to available categories
      setAvailableCategories([...availableCategories, newCategory]);
      
      // Auto-select the newly created category
      addMedicineForm.setFieldsValue({ category_id: newCategory.id });
      
      message.success('Category created successfully');
      setCategoryModalVisible(false);
      categoryForm.resetFields();
    } catch (error: any) {
      console.error('Error creating category:', error);
      message.error(error.response?.data?.error?.message || 'Failed to create category');
    }
  };

  // Debug: Check component state
  console.log('MedicineManagement render - branchId:', branchId);
  console.log('MedicineManagement render - addMedicineModalVisible:', addMedicineModalVisible);

  const getStockStatus = (quantity: number, minLevel: number) => {
    if (quantity === 0) return { status: 'error', text: 'Out of Stock' };
    if (quantity <= minLevel) return { status: 'warning', text: 'Low Stock' };
    return { status: 'success', text: 'In Stock' };
  };

  const calculateFinalPrice = (unitPrice: number | string, hikePercent: number = 0, discountPercent: number = 0) => {
    const numericPrice = typeof unitPrice === 'string' ? parseFloat(unitPrice) : (unitPrice || 0);
    const hikeAmount = (numericPrice * (hikePercent || 0)) / 100;
    const discountAmount = (numericPrice * (discountPercent || 0)) / 100;
    return numericPrice + hikeAmount - discountAmount;
  };

  const columns = [
    {
      title: 'Medicine Name',
      dataIndex: 'medicine_name',
      key: 'medicine_name',
      render: (text: string, record: Medicine) => {
        const hasManufacturer = record.manufacturer && record.manufacturer !== 'Unknown' && record.manufacturer !== 'N/A';
        const hasBatchNumber = record.batch_number && record.batch_number !== 'N/A';
        const showSubInfo = hasManufacturer || hasBatchNumber;
        
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            {showSubInfo && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                {hasManufacturer && hasBatchNumber 
                  ? `${record.manufacturer} | ${record.batch_number}`
                  : hasManufacturer 
                    ? record.manufacturer
                    : record.batch_number
                }
              </div>
            )}
            {record.status && record.status === 'Deleted' && (
              <Tag color="red">Deleted</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Shelf Index',
      dataIndex: 'notes',
      key: 'notes',
      width: 120,
      render: (notes: string) => (
        <span style={{ fontSize: '13px' }}>{notes || '-'}</span>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (price: number | string, record: Medicine) => {
        const numericPrice = typeof price === 'string' ? parseFloat(price) : (price || 0);
        const finalPrice = calculateFinalPrice(numericPrice, record.hike_percent || 0, record.discount_percent || 0);
        const hasHike = record.hike_percent && record.hike_percent > 0;
        const hasDiscount = record.discount_percent && record.discount_percent > 0;
        
        return (
          <div>
            <div style={{ fontSize: '12px', color: '#666' }}>Base: ₹{numericPrice.toFixed(2)}</div>
            {(hasHike || hasDiscount) ? (
              <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                Final: ₹{finalPrice.toFixed(2)}
                {hasHike && <span style={{ color: '#52c41a' }}> (+{record.hike_percent}%)</span>}
                {hasDiscount && <span style={{ color: '#ff4d4f' }}> (-{record.discount_percent}%)</span>}
              </div>
            ) : (
              <div style={{ fontWeight: 'bold' }}>₹{finalPrice.toFixed(2)}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Stock',
      dataIndex: 'quantity_available',
      key: 'quantity_available',
      render: (quantity: number, record: Medicine) => {
        const expiredQty = record.expired_quantity || 0;
        const destroyedQty = record.destroyed_quantity || 0;
        const inStockQty = calculateInStockQuantity(quantity, expiredQty, destroyedQty);
        const stockStatus = getStockStatus(inStockQty, record.min_stock_level || 0);
        
        return (
          <div>
            <Tag color={stockStatus.status}>{stockStatus.text}</Tag>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              <div>In Stock: {inStockQty} units</div>
              <div style={{ color: '#666' }}>
                Total: {quantity} | Expired: {expiredQty} | Destroyed: {destroyedQty}
              </div>
              <div style={{ color: '#999', fontSize: '10px' }}>
                Min Level: {record.min_stock_level || 0}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Expiry',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: (date: string | undefined) => {
        if (!date) {
          return <Tag color="default" icon={<CalendarOutlined />}>N/A</Tag>;
        }
        
        const expiryDate = new Date(date);
        const today = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        let color = 'default';
        if (daysUntilExpiry < 0) color = 'red';
        else if (daysUntilExpiry < 30) color = 'orange';
        else if (daysUntilExpiry < 90) color = 'yellow';
        
        return (
          <Tag color={color} icon={<CalendarOutlined />}>
            {daysUntilExpiry < 0 ? 'Expired' : `${daysUntilExpiry} days`}
          </Tag>
        );
      },
    },
    {
      title: 'Expired Qty',
      dataIndex: 'expired_quantity',
      key: 'expired_quantity',
      width: 100,
      render: (quantity: number) => (
        <span>{quantity || 0}</span>
      ),
    },
    {
      title: 'Destroyed Qty',
      dataIndex: 'destroyed_quantity',
      key: 'destroyed_quantity',
      width: 100,
      render: (quantity: number) => (
        <span>{quantity || 0}</span>
      ),
    },
    {
      title: 'Hike %',
      dataIndex: 'hike_percent',
      key: 'hike_percent',
      width: 80,
      render: (percent: number) => (
        <span>{percent || 0}%</span>
      ),
    },
    {
      title: 'Discount %',
      dataIndex: 'discount_percent',
      key: 'discount_percent',
      width: 80,
      render: (percent: number) => (
        <span>{percent || 0}%</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Medicine) => {
        const isInAddToCartMode = addToCartMode[record.branch_medicine_id || 0];
        const quantity = cartQuantities[record.branch_medicine_id || 0] || 1;
        
        return (
          <Space>
            {record.status !== 'Deleted' && (
              <>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  title="Edit"
                />
                {/* <Popconfirm
                  title="Soft delete medicine?"
                  description="This will soft delete the medicine. You can restore it later. Are you sure?"
                  onConfirm={() => handleDelete(record.medicine_id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    title="Soft Delete"
                  />
                </Popconfirm> */}
              </>
            )}
            {record.status === 'Deleted' && (
              <Popconfirm
                title="Restore medicine?"
                description="This will restore the soft deleted medicine. Are you sure?"
                onConfirm={() => handleRestore(record.medicine_id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  title="Restore Medicine"
                  style={{ color: '#52c41a' }}
                >
                  Restore
                </Button>
              </Popconfirm>
            )}
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              title="View Details"
            />
            {showAddToCart && (
              <>
                {!isInAddToCartMode ? (
                  <Button
                    type="primary"
                    size="small"
                    icon={<MedicineBoxOutlined />}
                    onClick={() => toggleAddToCartMode(record.branch_medicine_id || 0)}
                    disabled={record.quantity_available === 0}
                    title="Add to Cart"
                  >
                    Add to Cart
                  </Button>
                ) : (
                  <Space.Compact>
                    <InputNumber
                      min={1}
                      max={record.quantity_available}
                      value={quantity}
                      size="small"
                      style={{ width: '60px' }}
                      disabled={record.quantity_available === 0}
                      onChange={(value) => handleQuantityChange(record.branch_medicine_id || 0, value || 1)}
                      onKeyPress={(e) => handleKeyPress(e, record)}
                      autoFocus
                    />
                    <Button
                      type="primary"
                      size="small"
                      icon={<MedicineBoxOutlined />}
                      onClick={() => handleAddToCartFromInput(record)}
                      disabled={record.quantity_available === 0}
                      title="Add to Cart"
                    />
                  </Space.Compact>
                )}
              </>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Seller',
      dataIndex: 'seller_name',
      key: 'seller_name',
      render: (text: string, record: Medicine) => {
        if (!text || text === 'Unknown Seller') return <span style={{ color: '#999' }}>N/A</span>;
        
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            {record.seller_contact_person && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                {record.seller_contact_person}
              </div>
            )}
            {record.seller_phone && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                {record.seller_phone}
              </div>
            )}
          </div>
        );
      },
    },
  ];


  return (
    <div>
      {/* Patient Search Section */}
      <PatientSearch 
        onPatientSelect={handlePatientSelect}
        showCreateOption={true}
        placeholder="Search patient by name or phone number to proceed with medicine selection..."
      />

      <Card>
        <div style={{ marginBottom: '16px' }}>
          <Title level={4}>
            <MedicineBoxOutlined /> {
              userRole === 'branch' ? 'Branch Medicine Inventory' : 
              userRole === 'seller' ? 'Seller Medicine Inventory' : 
              'Medicine Inventory'
            }
          </Title>
          {(userRole === 'branch' || branchId) && (
            <p style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
              {filterType && filterId 
                ? `Showing medicines for ${filterType} ID: ${filterId}`
                : 'Showing medicines available in this branch with current stock levels'
              }
            </p>
          )}
          {userRole === 'seller' && (
            <p style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
              Showing medicines available from this seller with current stock levels
            </p>
          )}
          {selectedPatient && (
            <Alert
              message={`Selected Patient: ${selectedPatient.name}`}
              description={`Phone: ${selectedPatient.phoneNumber} | ID: ${selectedPatient.user_id}`}
              type="info"
              showIcon
              style={{ marginTop: '8px' }}
            />
          )}
        </div>

        <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Input
            placeholder="Search medicines by name, manufacturer, or category..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 400 }}
            allowClear
          />
          {(userRole === 'branch' || branchId) ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                console.log('Button clicked! branchId:', branchId, 'userRole:', userRole);
                handleOpenAddMedicineModal();
              }}
            >
              Add Medicine to Inventory
            </Button>
          ) : userRole === 'seller' ? (
            <div style={{ padding: '8px', color: '#666', fontSize: '14px' }}>
              Use the main Medicines page to add new medicines to your inventory
            </div>
          ) : (
            <div style={{ padding: '8px', color: 'red' }}>
              Debug: userRole is {userRole}, branchId is {branchId === undefined ? 'undefined' : branchId === null ? 'null' : String(branchId)}
            </div>
          )}
          {/* <Button
            type={showDeleted ? 'primary' : 'default'}
            onClick={() => setShowDeleted(!showDeleted)}
            size="small"
          >
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </Button> */}
        </div>

        {(userRole === 'branch' || branchId) && (!filteredMedicines || filteredMedicines.length === 0) && !loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <MedicineBoxOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Title level={4} style={{ color: '#999' }}>No Medicines Available</Title>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              This branch doesn't have any medicines in inventory yet.
            </p>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                console.log('Button clicked from empty state! branchId:', branchId, 'userRole:', userRole);
                handleOpenAddMedicineModal();
              }}
              size="large"
            >
              Add First Medicine
            </Button>
          </div>
        ) : userRole === 'seller' && (!filteredMedicines || filteredMedicines.length === 0) && !loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <MedicineBoxOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Title level={4} style={{ color: '#999' }}>No Medicines Available</Title>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              You don't have any medicines in your inventory yet.
            </p>
            <p style={{ color: '#999', fontSize: '14px' }}>
              Use the main Medicines page to add new medicines to your inventory.
            </p>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={Array.isArray(filteredMedicines) ? filteredMedicines : []}
            loading={loading}
            rowKey={(record: any) => record.branch_medicine_id || record.medicine_id}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} medicines`,
              pageSizeOptions: ['5', '10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize || prev.pageSize
                }));
              },
              onShowSizeChange: (current, size) => {
                setPagination(prev => ({
                  ...prev,
                  current: 1,
                  pageSize: size
                }));
              }
            }}
            scroll={{ x: 800 }}
            size="small"
          />
        )}
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Medicine"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => form.submit()}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Form.Item
            name="unit_price"
            label="Unit Price (₹)"
            rules={[{ required: true, message: 'Please enter unit price' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              prefix="₹"
            />
          </Form.Item>


          <Form.Item
            name="hike_percent"
            label="Hike Percentage (%)"
            rules={[{ required: true, message: 'Please enter hike percentage' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              precision={2}
              suffix="%"
            />
          </Form.Item>

          <Form.Item
            name="discount_percent"
            label="Discount Percentage (%)"
            rules={[{ required: true, message: 'Please enter discount percentage' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              precision={2}
              suffix="%"
            />
          </Form.Item>

          <Form.Item
            name="expired_quantity"
            label="Expired Quantity"
            rules={[{ required: true, message: 'Please enter expired quantity' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={0}
            />
          </Form.Item>

          <Form.Item
            name="destroyed_quantity"
            label="Destroyed Quantity"
            rules={[{ required: true, message: 'Please enter destroyed quantity' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={0}
            />
          </Form.Item>

          <Form.Item
            name="quantity_available"
            label="Stock Quantity"
            rules={[{ required: true, message: 'Please enter stock quantity' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={0}
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Shelf Index"
          >
            <Input.TextArea rows={3} placeholder="Enter shelf index for this medicine" />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal
        title="Medicine Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        width={600}
        footer={
          showAddToCart && selectedMedicine && selectedMedicine.quantity_available > 0 ? (
            <Space>
              <InputNumber
                min={1}
                max={selectedMedicine.quantity_available}
                value={addToCartQuantity}
                onChange={(value) => setAddToCartQuantity(value || 1)}
                style={{ width: '80px' }}
              />
              <Button type="primary" onClick={() => handleAddToCart()}>
                Add to Cart
              </Button>
            </Space>
          ) : null
        }
      >
        {selectedMedicine && (
          <div>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Medicine Name">
                {selectedMedicine.medicine_name}
              </Descriptions.Item>
              <Descriptions.Item label="Manufacturer">
                {selectedMedicine.manufacturer}
              </Descriptions.Item>
              <Descriptions.Item label="Batch Number">
                {selectedMedicine.batch_number}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                <Tag color="blue">{selectedMedicine.category_name}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Base Price">
                ₹{(typeof selectedMedicine.unit_price === 'string' ? parseFloat(selectedMedicine.unit_price) : (selectedMedicine.unit_price || 0)).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Hike Percentage">
                {selectedMedicine.hike_percent || 0}%
              </Descriptions.Item>
              <Descriptions.Item label="Discount Percentage">
                {selectedMedicine.discount_percent || 0}%
              </Descriptions.Item>
              <Descriptions.Item label="Final Price">
                <strong style={{ color: '#52c41a', fontSize: '16px' }}>
                  ₹{calculateFinalPrice(selectedMedicine.unit_price, selectedMedicine.hike_percent || 0, selectedMedicine.discount_percent || 0).toFixed(2)}
                </strong>
                {(selectedMedicine.hike_percent || selectedMedicine.discount_percent) && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    Base: ₹{(typeof selectedMedicine.unit_price === 'string' ? parseFloat(selectedMedicine.unit_price) : (selectedMedicine.unit_price || 0)).toFixed(2)}
                    {selectedMedicine.hike_percent && selectedMedicine.hike_percent > 0 && ` + ${selectedMedicine.hike_percent}%`}
                    {selectedMedicine.discount_percent && selectedMedicine.discount_percent > 0 && ` - ${selectedMedicine.discount_percent}%`}
                  </div>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Expiry Date">
                {selectedMedicine.expiry_date ? new Date(selectedMedicine.expiry_date).toLocaleDateString() : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Total Stock">
                {selectedMedicine.quantity_available} units
              </Descriptions.Item>
              <Descriptions.Item label="Expired Quantity">
                {selectedMedicine.expired_quantity || 0} units
              </Descriptions.Item>
              <Descriptions.Item label="Destroyed Quantity">
                {selectedMedicine.destroyed_quantity || 0} units
              </Descriptions.Item>
              <Descriptions.Item label="Available Stock">
                <Tag color={getStockStatus(calculateInStockQuantity(selectedMedicine.quantity_available, selectedMedicine.expired_quantity || 0, selectedMedicine.destroyed_quantity || 0), selectedMedicine.min_stock_level || 0).status}>
                  {calculateInStockQuantity(selectedMedicine.quantity_available, selectedMedicine.expired_quantity || 0, selectedMedicine.destroyed_quantity || 0)} units
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Minimum Stock Level">
                {selectedMedicine.min_stock_level || 0} units
              </Descriptions.Item>
            </Descriptions>

            {selectedMedicine.quantity_available <= (selectedMedicine.min_stock_level || 0) && (
              <Alert
                message="Low Stock Alert"
                description={`This medicine is running low on stock. Current: ${selectedMedicine.quantity_available}, Minimum: ${selectedMedicine.min_stock_level || 0}`}
                type="warning"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        )}
      </Modal>

      {/* Add Medicine Modal for Branches */}
      {(userRole === 'branch' || branchId) && (
        <Modal
          title="Create New Medicine"
          open={addMedicineModalVisible}
          onCancel={() => {
            setAddMedicineModalVisible(false);
            addMedicineForm.resetFields();
            createMedicineForm.resetFields();
            setIsCreatingNewMedicine(false);
          }}
          onOk={() => {
            console.log('Modal OK button clicked');
            console.log('Using Create New Medicine form');
            console.log('Create form values:', createMedicineForm.getFieldsValue());
            console.log('Create form validation:', createMedicineForm.getFieldsError());
            createMedicineForm.submit();
          }}
          width={700}
        >
          <Form
            form={createMedicineForm}
            layout="vertical"
            onFinish={handleAddMedicineSubmit}
            onFinishFailed={(errorInfo) => {
              console.log('Create New Medicine form validation failed:', errorInfo);
              message.error('Please fill in all required fields');
            }}
          >
            <Form.Item
              name="medicine_name"
              label="Medicine Name"
              rules={[{ required: true, message: 'Please enter medicine name' }]}
            >
              <Input placeholder="Enter medicine name" />
            </Form.Item>

            <Form.Item
              name="category_id"
              label="Category"
                      rules={[{ required: true, message: 'Please select a category' }]}
            >
              <Select
                placeholder="Select category"
                        showSearch
                        loading={loadingCategories}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        dropdownRender={(menu) => (
                          <div>
                            {menu}
                            <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                              <Button
                                type="text"
                                icon={<PlusOutlined />}
                                onClick={() => setCategoryModalVisible(true)}
                                style={{ width: '100%', textAlign: 'left', fontWeight: 500 }}
                              >
                                Add New Category
                              </Button>
                            </div>
                          </div>
                        )}
                        options={availableCategories.map(cat => ({
                          value: cat.id,
                          label: cat.name
                        }))}
                      />
                    </Form.Item>

                    <Form.Item
                      name="manufacturer"
                      label="Manufacturer"
                    >
                      <Input placeholder="Enter manufacturer name" />
                    </Form.Item>

                    <Form.Item
                      name="description"
                      label="Description"
                    >
                      <Input.TextArea rows={2} placeholder="Enter medicine description" />
                    </Form.Item>

                    <Form.Item
                      name="seller"
                      label="Select Supplier (Seller)"
                      rules={[{ required: true, message: 'Please select a supplier' }]}
                    >
                      <Select
                        placeholder="Select the seller who will supply this medicine"
                        showSearch
                        loading={loadingSellers}
                        optionFilterProp="children"
                        onChange={(value) => setSelectedSellerId(value)}
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={availableSellers.map(seller => ({
                          value: seller.id,
                          label: `${seller.name}${seller.contact ? ` - ${seller.contact}` : ''}`
                        }))}
                      />
                    </Form.Item>

                    <Form.Item
                      name="current_stock"
                      label="Initial Stock Quantity"
                      rules={[{ required: true, message: 'Please enter stock quantity' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        precision={0}
                        placeholder="Enter initial stock"
                      />
                    </Form.Item>

                    <Form.Item
                      name="unit_price"
                      label="Unit Price (₹)"
                      rules={[{ required: true, message: 'Please enter unit price' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        prefix="₹"
                        placeholder="Enter unit price"
                      />
                    </Form.Item>

                    <Form.Item
                      name="hike_percent"
                      label="Hike Percentage (%)"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        max={100}
                        precision={2}
                        suffix="%"
                        placeholder="0"
                      />
                    </Form.Item>

                    <Form.Item
                      name="discount_percent"
                      label="Discount Percentage (%)"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        max={100}
                        precision={2}
                        suffix="%"
                        placeholder="0"
                      />
                    </Form.Item>

                    <Form.Item
                      name="expiry_date"
                      label="Expiry Date"
                      rules={[{ required: true, message: 'Please select an expiry date' }]}
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      name="notes"
                      label="Shelf Index"
                    >
                      <Input.TextArea rows={3} placeholder="Enter shelf index for this medicine" />
                    </Form.Item>
          </Form>
        </Modal>
      )}

      {/* Category Creation Modal */}
      <Modal
        title="Add New Category"
        open={categoryModalVisible}
        onOk={handleCategoryCreate}
        onCancel={() => {
          setCategoryModalVisible(false);
          categoryForm.resetFields();
        }}
        width={500}
      >
        <Form
          form={categoryForm}
          layout="vertical"
        >
          <Form.Item
            name="category_name"
            label="Category Name"
            rules={[{ required: true, message: 'Please enter category name' }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <Input.TextArea rows={3} placeholder="Enter category description" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MedicineManagement;
